import React from 'react';
import { FiX, FiStar, FiFlag, FiEyeOff, FiRefreshCw, FiTrash2, FiAlertTriangle } from 'react-icons/fi';

const ReviewDetailModal = ({ review, onClose, onModerate, onToggleFeatured }) => {
  if (!review) return null;

  const formatDate = (date) => {
    return new Date(date).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      active: 'bg-green-100 text-green-800',
      flagged: 'bg-yellow-100 text-yellow-800',
      hidden: 'bg-gray-100 text-gray-600',
      deleted: 'bg-red-100 text-red-800'
    };
    return colors[status] || colors.active;
  };

  const getFlagReasonLabel = (reason) => {
    const labels = {
      spam: 'Spam',
      inappropriate: 'Inappropriate Content',
      'off-topic': 'Off Topic',
      promotional: 'Promotional',
      other: 'Other'
    };
    return labels[reason] || reason;
  };

  const getAutoFlagLabel = (reason) => {
    const labels = {
      profanity: '🚫 Profanity detected',
      spam_detected: '🤖 Spam patterns detected',
      too_short: '📝 Review too short',
      repeated_chars: '🔄 Repeated characters',
      contains_link: '🔗 Contains links'
    };
    return labels[reason] || reason;
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <h2 className="text-lg font-semibold text-gray-900">Review Details</h2>
              {review.isFeatured && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                  <FiStar className="w-3 h-3 mr-1 fill-current" />
                  Featured
                </span>
              )}
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
            >
              <FiX size={20} />
            </button>
          </div>

          {/* Content */}
          <div className="px-6 py-4 overflow-y-auto max-h-[calc(90vh-180px)]">
            {/* Product Info */}
            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              <h3 className="text-sm font-medium text-gray-500 mb-1">Product</h3>
              <div className="text-lg font-medium text-gray-900">
                {review.product?.name || 'Unknown Product'}
              </div>
              <div className="text-sm text-gray-600">
                {review.product?.brand}
              </div>
            </div>

            {/* Rating & Repurchase */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="p-4 bg-gray-50 rounded-lg">
                <h3 className="text-sm font-medium text-gray-500 mb-2">Rating</h3>
                <div className="flex items-center">
                  <span className="text-2xl font-bold text-gray-900">{review.rating}</span>
                  <div className="ml-2 flex">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <FiStar
                        key={star}
                        className={`w-5 h-5 ${
                          star <= review.rating 
                            ? 'text-yellow-400 fill-current' 
                            : 'text-gray-300'
                        }`}
                      />
                    ))}
                  </div>
                </div>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <h3 className="text-sm font-medium text-gray-500 mb-2">Would Repurchase</h3>
                <div className={`text-2xl font-bold ${
                  review.wouldRepurchase === 'yes' ? 'text-green-600' :
                  review.wouldRepurchase === 'no' ? 'text-red-600' : 'text-yellow-600'
                }`}>
                  {review.wouldRepurchase === 'yes' ? '✓ Yes' : 
                   review.wouldRepurchase === 'no' ? '✗ No' : '~ Maybe'}
                </div>
              </div>
            </div>

            {/* Review Text */}
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-500 mb-2">Review</h3>
              <div className="p-4 bg-white border border-gray-200 rounded-lg">
                {review.quickTake ? (
                  <p className="text-gray-800 whitespace-pre-wrap">{review.quickTake}</p>
                ) : (
                  <p className="text-gray-400 italic">No review text provided</p>
                )}
              </div>
            </div>

            {/* Reviewer Info */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div>
                <h3 className="text-xs font-medium text-gray-500 mb-1">Nickname</h3>
                <p className="text-sm text-gray-900">{review.nickname || 'Anonymous'}</p>
              </div>
              <div>
                <h3 className="text-xs font-medium text-gray-500 mb-1">Skin Type</h3>
                <p className="text-sm text-gray-900 capitalize">{review.skinType || '—'}</p>
              </div>
              <div>
                <h3 className="text-xs font-medium text-gray-500 mb-1">Age Range</h3>
                <p className="text-sm text-gray-900">{review.ageRange || '—'}</p>
              </div>
              <div>
                <h3 className="text-xs font-medium text-gray-500 mb-1">Submitted</h3>
                <p className="text-sm text-gray-900">{formatDate(review.createdAt)}</p>
              </div>
            </div>

            {/* Status & Flags Section */}
            <div className="mb-6 p-4 border border-gray-200 rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-gray-700">Status & Flags</h3>
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(review.status)}`}>
                  {review.status}
                </span>
              </div>

              {/* Auto-flag reasons */}
              {review.isAutoFlagged && review.autoFlagReasons?.length > 0 && (
                <div className="mb-3">
                  <h4 className="text-xs font-medium text-orange-700 mb-2 flex items-center">
                    <FiAlertTriangle className="w-3 h-3 mr-1" />
                    Auto-Flagged Reasons
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {review.autoFlagReasons.map((reason, idx) => (
                      <span
                        key={idx}
                        className="px-2 py-1 text-xs bg-orange-50 text-orange-700 rounded"
                      >
                        {getAutoFlagLabel(reason)}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* User flag reasons */}
              {review.flagCount > 0 && (
                <div>
                  <h4 className="text-xs font-medium text-yellow-700 mb-2 flex items-center">
                    <FiFlag className="w-3 h-3 mr-1" />
                    User Reports ({review.flagCount})
                  </h4>
                  {review.flagReasons?.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {review.flagReasons.map((flag, idx) => (
                        <span
                          key={idx}
                          className="px-2 py-1 text-xs bg-yellow-50 text-yellow-700 rounded"
                        >
                          {getFlagReasonLabel(flag.reason)}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-500">No specific reasons recorded</p>
                  )}
                </div>
              )}

              {!review.isAutoFlagged && review.flagCount === 0 && (
                <p className="text-sm text-gray-500">No flags or issues detected</p>
              )}
            </div>

            {/* Engagement Stats */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="p-3 bg-green-50 rounded-lg text-center">
                <div className="text-xl font-bold text-green-700">{review.helpfulVotes || 0}</div>
                <div className="text-xs text-green-600">Helpful Votes</div>
              </div>
              <div className="p-3 bg-red-50 rounded-lg text-center">
                <div className="text-xl font-bold text-red-700">{review.notHelpfulVotes || 0}</div>
                <div className="text-xs text-red-600">Not Helpful Votes</div>
              </div>
            </div>

            {/* Moderation Notes */}
            {review.moderationNotes && (
              <div className="mb-6">
                <h3 className="text-sm font-medium text-gray-500 mb-2">Moderation Notes</h3>
                <div className="p-3 bg-gray-50 rounded-lg text-sm text-gray-700">
                  {review.moderationNotes}
                </div>
                {review.moderatedAt && (
                  <p className="text-xs text-gray-400 mt-1">
                    Last moderated: {formatDate(review.moderatedAt)}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Actions Footer */}
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex gap-2">
                {/* Feature Toggle */}
                <button
                  onClick={onToggleFeatured}
                  className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                    review.isFeatured
                      ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                      : 'bg-white border border-gray-300 text-gray-700 hover:bg-yellow-50'
                  }`}
                >
                  <FiStar className={`w-4 h-4 mr-2 ${review.isFeatured ? 'fill-current' : ''}`} />
                  {review.isFeatured ? 'Unfeature' : 'Feature'}
                </button>
              </div>

              <div className="flex gap-2">
                {review.status === 'active' ? (
                  <button
                    onClick={() => onModerate('hide')}
                    className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    <FiEyeOff className="w-4 h-4 mr-2" />
                    Hide
                  </button>
                ) : review.status !== 'deleted' ? (
                  <button
                    onClick={() => onModerate('restore')}
                    className="inline-flex items-center px-3 py-2 text-sm font-medium text-green-700 bg-green-100 rounded-lg hover:bg-green-200"
                  >
                    <FiRefreshCw className="w-4 h-4 mr-2" />
                    Restore
                  </button>
                ) : null}

                {review.status !== 'deleted' && (
                  <button
                    onClick={() => {
                      if (window.confirm('Delete this review permanently?')) {
                        onModerate('delete');
                      }
                    }}
                    className="inline-flex items-center px-3 py-2 text-sm font-medium text-red-700 bg-red-100 rounded-lg hover:bg-red-200"
                  >
                    <FiTrash2 className="w-4 h-4 mr-2" />
                    Delete
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewDetailModal;

